import React from 'react';
import styled from 'styled-components';
import { colors, borderRadius, shadows, zIndex, typography } from '../styles/designTokens';
import { Payment, PAYMENT_LIMIT_OPTIONS } from '../models/PaymentModels';
import { formatDateTimeKST } from '../utils/dateUtils';
import { formatCurrency } from '../utils/numberFormatter';
import Pagination from './Pagination';

// 테이블 섹션 컨테이너 스타일
const TableSection = styled.div`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.base};
  overflow: hidden;
`;

// 테이블 헤더 스타일
const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

// 헤더 정보 스타일
const HeaderInfo = styled.div`
  h3 {
    font-size: 18px;
    font-weight: 700;
    color: ${colors.secondary[800]};
    margin: 0 0 4px 0;
  }

  .subtitle {
    font-size: 14px;
    color: ${colors.semantic.text.tertiary};
    margin: 0;
  }
`;

// 페이지 크기 선택 컨트롤 스타일
const LimitControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  label {
    font-size: 14px;
    color: ${colors.semantic.text.secondary};
    font-weight: 500;
    margin: 0;
  }

  select {
    padding: 8px 12px;
    border: 1px solid ${colors.neutral[300]};
    border-radius: ${borderRadius.md};
    background: ${colors.semantic.background.primary};
    color: ${colors.semantic.text.secondary};
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: ${colors.primary[500]};
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
      background: ${colors.neutral[50]};
      color: ${colors.semantic.text.disabled};
      cursor: not-allowed;
    }
  }
`;

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  overflow-x: auto;
`;

// 테이블 스타일
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  
  th,
  td {
    padding: 16px 20px;
    text-align: left;
    border-bottom: 1px solid ${colors.neutral[100]};
  }

  th {
    background: ${colors.neutral[50]};
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid ${colors.neutral[200]};
    position: sticky;
    top: 0;
    z-index: ${zIndex.docked};
  }

  tbody tr {
    transition: background-color 0.2s;

    &:hover {
      background: ${colors.neutral[50]};
    }
    
    &:last-child td {
      border-bottom: none;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    
    th, td {
      padding: 12px 16px;
    }
  }
`;

// 날짜 텍스트 스타일
const DateText = styled.span`
  font-size: 12px;
  color: ${colors.semantic.text.tertiary};
  font-weight: 500;
`;

// 유저명 텍스트 스타일
const UserText = styled.span`
  font-weight: 600;
  color: ${colors.secondary[800]};
`;

// 상품명 텍스트 스타일
const ProductText = styled.span`
  font-weight: 500;
  color: ${colors.semantic.text.secondary};
`;

// 금액 텍스트 스타일
const AmountText = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: ${colors.success[600]};
`;

// 결제수단 배지 스타일
const PaymentMethodBadge = styled.span<{ method: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid;
  
  ${props => {
    switch (props.method) {
      case '카드':
      case 'CARD':
        return `
          color: ${colors.primary[800]};
          background-color: ${colors.primary[100]};
          border-color: ${colors.primary[300]};
        `;
      case '계좌이체':
      case 'BANK_TRANSFER':
        return `
          color: ${colors.secondary[700]};
          background-color: ${colors.secondary[100]};
          border-color: ${colors.secondary[300]};
        `;
      default:
        return `
          color: ${colors.semantic.text.tertiary};
          background-color: ${colors.neutral[50]};
          border-color: ${colors.neutral[200]};
        `;
    }
  }}
`;

// 카드 정보 텍스트 스타일
const CardInfoText = styled.span`
  font-size: 12px;
  color: ${colors.semantic.text.tertiary};
  font-family: ${typography.fontFamily.mono};
`;

// 로딩 컨테이너 스타일
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: ${colors.semantic.background.primary};
`;

// 로딩 스피너 스타일
const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${colors.neutral[100]};
  border-top: 3px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 빈 상태 컨테이너 스타일
const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
  text-align: center;
  background: ${colors.semantic.background.primary};

  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
    color: ${colors.semantic.text.disabled};
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: ${colors.semantic.text.secondary};
  }

  p {
    font-size: 14px;
    margin: 0;
    color: ${colors.semantic.text.tertiary};
    line-height: 1.5;
  }
`;

// 에러 컨테이너 스타일
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
  text-align: center;
  background: ${colors.error[50]};

  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    color: ${colors.error[600]};
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: ${colors.error[800]};
  }

  p {
    font-size: 14px;
    margin: 0;
    color: ${colors.error[900]};
    line-height: 1.5;
  }
`;

// 페이지네이션 컨테이너 스타일
const PaginationContainer = styled.div`
  padding: 20px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

// Props 인터페이스 (ViewModel에서 데이터 전달받음 - API 중복 호출 방지)
interface PaymentListTableProps {
  payments: Payment[] | null | undefined;
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

// 결제 목록 테이블 컴포넌트
const PaymentListTable: React.FC<PaymentListTableProps> = ({
  payments,
  loading,
  error,
  currentPage,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange
}) => {
  // 디버깅: 받은 데이터 확인
  console.log('PaymentListTable - payments:', payments);
  console.log('PaymentListTable - loading:', loading);
  console.log('PaymentListTable - error:', error);
  console.log('PaymentListTable - total:', total);
  // 로딩 상태 표시
  if (loading) {
    return (
      <TableSection>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </TableSection>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <TableSection>
        <ErrorContainer>
          <div className="icon">⚠️</div>
          <h3>데이터 로드 실패</h3>
          <p>{error}</p>
        </ErrorContainer>
      </TableSection>
    );
  }

  return (
    <TableSection>
      {/* 테이블 헤더 */}
      <TableHeader>
        <HeaderInfo>
          <h3>결제 내역</h3>
          <p className="subtitle">
            총 {total.toLocaleString()}개의 결제 내역
          </p>
        </HeaderInfo>

        {/* 페이지 크기 선택 */}
        <LimitControl>
          <label htmlFor="limitSelect">페이지당 표시:</label>
          <select
            id="limitSelect"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            disabled={loading}
          >
            {PAYMENT_LIMIT_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}개
              </option>
            ))}
          </select>
        </LimitControl>
      </TableHeader>

      {/* 테이블 내용 */}
      {!payments || payments.length === 0 ? (
        <EmptyContainer>
          <div className="icon">💳</div>
          <h3>결제 내역이 없습니다</h3>
          <p>검색 조건을 변경하거나 다른 기간을 선택해보세요.</p>
          <p style={{ fontSize: '12px', color: colors.neutral[500], marginTop: '8px' }}>
            디버그: payments={payments ? `배열(길이: ${payments.length})` : 'null/undefined'}
          </p>
        </EmptyContainer>
      ) : (
        <>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '15%' }}>승인 날짜</th>
                  <th style={{ width: '15%' }}>결제유저명</th>
                  <th style={{ width: '20%' }}>결제상품명</th>
                  <th style={{ width: '15%' }}>결제금액</th>
                  <th style={{ width: '10%' }}>결제수단</th>
                  <th style={{ width: '10%' }}>카드사</th>
                  <th style={{ width: '15%' }}>카드번호</th>
                </tr>
              </thead>
              <tbody>
                {(payments || []).map((payment, index) => (
                  <tr key={index}>
                    <td>
                      <DateText>
                        {formatDateTimeKST(payment.승인날짜)}
                      </DateText>
                    </td>
                    <td>
                      <UserText>
                        {payment.결제유저명}
                      </UserText>
                    </td>
                    <td>
                      <ProductText>
                        {payment.결제상품명}
                      </ProductText>
                    </td>
                    <td>
                      <AmountText>
                        {formatCurrency(payment.결제금액)}
                      </AmountText>
                    </td>
                    <td>
                      <PaymentMethodBadge method={payment.결제수단}>
                        {payment.결제수단}
                      </PaymentMethodBadge>
                    </td>
                    <td>
                      <span>{payment.카드사}</span>
                    </td>
                    <td>
                      <CardInfoText>
                        {payment.카드번호}
                      </CardInfoText>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>

          {/* 페이지네이션 - 1페이지라도 표시 */}
          <PaginationContainer>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={onPageChange}
            />
          </PaginationContainer>
        </>
      )}
    </TableSection>
  );
};

export default PaymentListTable; 